import { useCallback, useRef, useState } from "react";

import {
  TableMetadata,
  useTable,
  Button,
  Tooltip,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as Download } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ReactComponent as DownloadWhite } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import {
  CodeBook,
  ExportTypeEnum,
  ExportFiltersData,
} from "@king-ict/eupisi-ui/models";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { getMonthsExportFile, searchImportantDateMonths } from "../../../api";
import { editSubsequentImportantDateUrl } from "../../../app";
import {
  AdministrationList,
  AdministrationListFiltersData,
  AdministrationListHeader,
  AdministrationListFilters,
} from "../../../components";
import styles from "./SubsequentImportantDateList.module.scss";

export const SubsequentImportantDateList = () => {
  const [months, setMonths] = useState<CodeBook[]>([]);

  const filtersRef = useRef<AdministrationListFiltersData>(
    {} as AdministrationListFiltersData
  );

  const [exportType, setExportType] = useState<number>(ExportTypeEnum.Excel);

  const { t } = useTranslation();
  const history = useHistory();

  const handleDetailsClicked = (id?: number, guid?: string) => {
    if (id) {
      history.push(
        editSubsequentImportantDateUrl(
          filtersRef.current.pedagogicalYearId!,
          id!
        )
      );
    }
  };

  const handleChangeFormat = (typeId: number) => {
    setExportType(typeId);
  };

  const handleDownloadDocument = () => {
    getMonthsExportFile({
      pedagogicalYearId: filtersRef.current.pedagogicalYearId,
      exportTypeId: exportType,
    } as ExportFiltersData);
  };

  const handleFiltersChanged = useCallback(
    async (filters: AdministrationListFiltersData) => {
      filtersRef.current = filters;

      setMonths(await searchImportantDateMonths(filters));
    },
    []
  );

  const [tableProps] = useTable<AdministrationListFiltersData>({
    data: months,
    totalItems: months.length,
    noHover: true,

    metadata: {
      rowKey: "id",
      columns: [
        {
          columnKey: "name",
          header: t("common.month"),
          accessor: "name",
        },
      ],
    } as TableMetadata<CodeBook>,
  });

  return (
    <>
      <AdministrationListHeader
        infoMessage={t("importantDates.subsequentInfoMessage")}
        title={t("importantDates.subsequentListTitle")}
      />
      <AdministrationListFilters
        isFilteredByYear
        callbackOnFiltersChanged
        onFiltersChanged={handleFiltersChanged}
      />
      <AdministrationList
        tableProps={tableProps}
        isCreateEnabled={false}
        isDeleteEnabled={false}
        isDetailsEnabled={true}
        onDetailsClicked={handleDetailsClicked}
      />
      {months?.length > 0 && (
        <Col
          md={12}
          className={`${styles.withoutPadding} ${styles.buttonsLayout}`}
        >
          <Tooltip
            overlay={
              <Row className={styles.printTooltipLayout}>
                <Col md={12} className={styles.printText}>
                  <Typography
                    variant="XLargeBold"
                    className={styles.printTitle}
                  >
                    {t("importantDates.printTitle")}
                  </Typography>
                  <Typography variant="base" className={styles.printFormat}>
                    {t("importantDates.printFormat")}
                  </Typography>
                  <Row md={12} className={styles.buttonPrint}>
                    <Col
                      md={6}
                      xs={6}
                      xl={6}
                      lg={6}
                      sm={6}
                      className={`${styles.btnRightSpace}`}
                    >
                      <Button
                        className={`${styles.fullwidth} ${styles.btnLeft}`}
                        size="sm"
                        color="primary"
                        outline={exportType !== ExportTypeEnum.Csv}
                        onClick={() => handleChangeFormat(ExportTypeEnum.Csv)}
                      >
                        {t("importantDates.csv")}
                      </Button>
                    </Col>
                    <Col
                      md={6}
                      xs={6}
                      xl={6}
                      lg={6}
                      sm={6}
                      className={`${styles.btnLeftSpace}`}
                    >
                      <Button
                        className={`${styles.fullwidth} ${styles.btnRight}`}
                        size="sm"
                        color="primary"
                        outline={exportType !== ExportTypeEnum.Excel}
                        onClick={() => handleChangeFormat(ExportTypeEnum.Excel)}
                      >
                        {t("importantDates.xls")}
                      </Button>
                    </Col>
                  </Row>
                  <Button
                    size="lg"
                    icon={<DownloadWhite />}
                    color="primary"
                    className={`${styles.buttonSpace}`}
                    onClick={handleDownloadDocument}
                  >
                    {t("request.initialConversation.download")}
                  </Button>
                </Col>
              </Row>
            }
            trigger={"click"}
            placement={"bottom"}
          >
            <Button
              size="sm"
              color="primary"
              outline
              icon={<Download />}
              className={`${styles.buttonSpace}`}
            >
              {t("request.initialConversation.download")}
            </Button>
          </Tooltip>
        </Col>
      )}
    </>
  );
};
